import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type {
  CreateScopeRuleRequest,
  ScopeRule,
  ScopeRuleEffect,
  UserAttributeDefinition,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface Props {
  dataSourceId: string;
  tableId: string;
  /** 매핑 컬럼 후보 — 현재 테이블의 컬럼명 배열. */
  tableColumns: string[];
}

/**
 * 데이터소스 테이블별 권한 룰 체인 빌더.
 * - 룰이 0개면 기존 user_scope_column 동작 유지 (안내문).
 * - 룰 1개 이상이면 그 체인이 우선. 매칭 없으면 거부.
 */
export function ScopeRulesEditor({ dataSourceId, tableId, tableColumns }: Props) {
  const qc = useQueryClient();
  const rulesQ = useQuery({
    queryKey: ['admin', 'scope-rules', dataSourceId, tableId],
    queryFn: () => api.adminScopeRulesList(dataSourceId, tableId),
  });
  const attrsQ = useQuery({
    queryKey: ['admin', 'user-attributes'],
    queryFn: api.adminUserAttributesList,
  });
  const rules = rulesQ.data?.rules ?? [];
  const defs = attrsQ.data?.definitions ?? [];

  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.adminReorderScopeRules(dataSourceId, tableId, ids),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'scope-rules', dataSourceId, tableId] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '순서 변경 실패'),
  });

  const del = useMutation({
    mutationFn: (ruleId: string) => api.adminDeleteScopeRule(dataSourceId, tableId, ruleId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'scope-rules', dataSourceId, tableId] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '삭제 실패'),
  });

  const create = useMutation({
    mutationFn: (body: CreateScopeRuleRequest) =>
      api.adminCreateScopeRule(dataSourceId, tableId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'scope-rules', dataSourceId, tableId] });
      setAdding(false);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : '룰 생성 실패'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...rules];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[idx];
    const b = next[target];
    if (!a || !b) return;
    next[idx] = b;
    next[target] = a;
    reorder.mutate(next.map((r) => r.id));
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-surface-muted/30 p-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">권한 룰 체인</h3>
        <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-3.5" />룰 추가
        </Button>
      </div>
      <p className="mt-1 text-xs text-foreground-muted">
        사용자 속성으로 매칭해 first-match 로 권한을 결정합니다. 룰이 비어있으면 위의
        user_scope_column 동작으로 폴백.
      </p>

      {error ? (
        <div className="mt-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <div className="mt-3 space-y-2">
        {rules.map((r, i) => (
          <RuleRow
            key={r.id}
            rule={r}
            index={i}
            total={rules.length}
            defs={defs}
            onMoveUp={() => move(i, -1)}
            onMoveDown={() => move(i, 1)}
            onDelete={() => {
              if (window.confirm('이 룰을 삭제하시겠습니까?')) del.mutate(r.id);
            }}
          />
        ))}

        {rules.length === 0 && !adding ? (
          <p className="text-xs text-foreground-muted">
            등록된 룰이 없습니다. user_scope_column 동작이 그대로 적용됩니다.
          </p>
        ) : null}

        {adding ? (
          <RuleForm
            defs={defs}
            tableColumns={tableColumns}
            onCancel={() => setAdding(false)}
            onSubmit={(b) => create.mutate(b)}
            pending={create.isPending}
          />
        ) : null}
      </div>
    </div>
  );
}

function RuleRow({
  rule,
  index,
  total,
  defs,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  rule: ScopeRule;
  index: number;
  total: number;
  defs: UserAttributeDefinition[];
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  const def = defs.find((d) => d.key === rule.attributeKey);
  const targetDef = rule.targetAttributeKey
    ? defs.find((d) => d.key === rule.targetAttributeKey)
    : null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm">
      <div className="flex flex-col gap-1">
        <IconBtn aria-label="위로" onClick={onMoveUp} disabled={index === 0}>
          <ArrowUp className="size-3.5" />
        </IconBtn>
        <IconBtn aria-label="아래로" onClick={onMoveDown} disabled={index === total - 1}>
          <ArrowDown className="size-3.5" />
        </IconBtn>
      </div>
      <div className="flex-1 min-w-0">
        <div>
          <span className="font-mono text-xs text-foreground-muted">if</span>{' '}
          <span className="font-medium">{def?.label ?? rule.attributeKey}</span>
          <span className="text-foreground-muted"> = </span>
          <span className="font-mono">{rule.attributeValue}</span>
        </div>
        <div className="mt-1 text-xs text-foreground-muted">
          {rule.effect === 'admin' ? (
            <span className="text-success">→ 관리자 권한 (필터 우회)</span>
          ) : (
            <span>
              → WHERE <span className="font-mono">{rule.targetTableColumn}</span> ={' '}
              <span className="font-mono">user.{targetDef?.label ?? rule.targetAttributeKey}</span>
            </span>
          )}
        </div>
      </div>
      <IconBtn aria-label="삭제" danger onClick={onDelete}>
        <Trash2 className="size-3.5" />
      </IconBtn>
    </div>
  );
}

function RuleForm({
  defs,
  tableColumns,
  onCancel,
  onSubmit,
  pending,
}: {
  defs: UserAttributeDefinition[];
  tableColumns: string[];
  onCancel: () => void;
  onSubmit: (body: CreateScopeRuleRequest) => void;
  pending: boolean;
}) {
  const [attributeKey, setAttributeKey] = useState(defs[0]?.key ?? '');
  const [attributeValue, setAttributeValue] = useState('');
  const [effect, setEffect] = useState<ScopeRuleEffect>('admin');
  const [targetAttributeKey, setTargetAttributeKey] = useState(defs[0]?.key ?? '');
  const [targetTableColumn, setTargetTableColumn] = useState(tableColumns[0] ?? '');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!attributeKey || !attributeValue) return;
    const body: CreateScopeRuleRequest = {
      attributeKey,
      attributeValue,
      effect,
      ...(effect === 'map' ? { targetAttributeKey, targetTableColumn } : {}),
    };
    onSubmit(body);
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-md border border-accent/40 bg-surface px-3 py-3 text-sm space-y-2"
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-foreground-muted">if</span>
        <select
          value={attributeKey}
          onChange={(e) => setAttributeKey(e.target.value)}
          className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
        >
          {defs.map((d) => (
            <option key={d.id} value={d.key}>
              {d.label} ({d.key})
            </option>
          ))}
        </select>
        <span className="text-foreground-muted">=</span>
        <input
          type="text"
          value={attributeValue}
          onChange={(e) => setAttributeValue(e.target.value)}
          className="h-8 flex-1 rounded-md border border-border bg-surface px-2 text-sm"
          placeholder="예: CEO"
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-foreground-muted">동작</span>
        <select
          value={effect}
          onChange={(e) => setEffect(e.target.value as ScopeRuleEffect)}
          className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
        >
          <option value="admin">관리자 (모든 행)</option>
          <option value="map">컬럼 매핑</option>
        </select>
      </div>

      {effect === 'map' ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-muted">WHERE</span>
          <select
            value={targetTableColumn}
            onChange={(e) => setTargetTableColumn(e.target.value)}
            className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
          >
            {tableColumns.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="text-foreground-muted">=</span>
          <span className="font-mono text-xs text-foreground-muted">user.</span>
          <select
            value={targetAttributeKey}
            onChange={(e) => setTargetAttributeKey(e.target.value)}
            className="h-8 rounded-md border border-border bg-surface px-2 text-sm"
          >
            {defs.map((d) => (
              <option key={d.id} value={d.key}>
                {d.label} ({d.key})
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          취소
        </Button>
        <Button type="submit" size="sm" disabled={pending || !attributeKey || !attributeValue}>
          {pending ? '추가 중…' : '룰 추가'}
        </Button>
      </div>
    </form>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  danger,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
} & React.AriaAttributes) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      className={cn(
        'size-6 inline-grid place-items-center rounded text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'hover:text-destructive' : '',
      )}
    >
      {children}
    </button>
  );
}
